import React, { useState } from 'react';
import { Phone, Mail, Send, Facebook, Instagram, Linkedin } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Card, CardContent } from '../ui/card';
import { siteConfig } from '../../data/mock';
import { useContent } from '../../context/ContentContext';
import { toast } from 'sonner';
import api, { formatApiErrorDetail } from '../../lib/api';

const CONTACT_IMG = 'https://static.prod-images.emergentagent.com/jobs/fc3829d4-353f-4fc4-b817-cd1ab3ff1600/images/af75810f8b775ec5333bce4b9881d7be995b6c8e47ff5e22c8de5f705e09d39a.jpeg';

const ContactPage = () => {
  const { c } = useContent();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await api.post('/contact', formData);
      toast.success('Thank you for your message! We will get back to you soon.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
      });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="pt-52">
      {/* Hero Section */}
      <section className="py-20 bg-[#f1e3eb]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="max-w-3xl">
              <span className="text-[#bd8cae] text-sm uppercase tracking-wider font-medium">
                {c('contact.hero.eyebrow', 'Get in Touch')}
              </span>
              <h1 className="font-serif text-4xl sm:text-5xl text-[#3c4a2a] mt-3 mb-6">
                {c('contact.hero.title', 'Contact Us')}
              </h1>
              <p className="text-lg text-[#3c4a2a]/80 leading-relaxed">
                {c('contact.hero.text', "We'd love to hear from you. Whether you have a question about membership, programs, or just want to say hello, we're here to help.")}
              </p>
            </div>
            <div className="aspect-[3/2] rounded-2xl overflow-hidden shadow-xl" data-testid="contact-hero-image">
              <img
                src={c('contact.hero.image', CONTACT_IMG)}
                alt="Women of the club connecting and laughing together"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Information */}
            <div className="lg:col-span-1">
              <h2 className="font-serif text-2xl text-[#3c4a2a] mb-8">
                Contact Information
              </h2>
              
              <div className="space-y-6">
                <Card className="bg-[#f1e3eb] border-0">
                  <CardContent className="p-6">
                    <div className="flex items-start">
                      <div className="w-12 h-12 rounded-full bg-[#bd8cae]/50 flex items-center justify-center mr-4 flex-shrink-0">
                        <Phone className="w-5 h-5 text-[#5d6b3c]" />
                      </div>
                      <div>
                        <h3 className="font-medium text-[#3c4a2a] mb-1">Phone</h3>
                        <a
                          href={`tel:${c('site.phone', siteConfig.phone)}`}
                          className="text-[#5d6b3c] hover:text-[#3c4a2a] text-sm"
                        >
                          {c('site.phone', siteConfig.phone)}
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-[#f1e3eb] border-0">
                  <CardContent className="p-6">
                    <div className="flex items-start">
                      <div className="w-12 h-12 rounded-full bg-[#bd8cae]/50 flex items-center justify-center mr-4 flex-shrink-0">
                        <Mail className="w-5 h-5 text-[#5d6b3c]" />
                      </div>
                      <div>
                        <h3 className="font-medium text-[#3c4a2a] mb-1">Email</h3>
                        <a
                          href={`mailto:${c('site.email', siteConfig.email)}`}
                          className="text-[#5d6b3c] hover:text-[#3c4a2a] text-sm"
                        >
                          {c('site.email', siteConfig.email)}
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Social Links */}
              <div className="mt-8">
                <h3 className="font-medium text-[#3c4a2a] mb-4">Follow Us</h3>
                <div className="flex space-x-4">
                  <a
                    href={siteConfig.socialLinks.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full bg-[#5d6b3c] flex items-center justify-center text-white hover:bg-[#3c4a2a] transition-colors"
                    aria-label="Facebook"
                  >
                    <Facebook className="w-5 h-5" />
                  </a>
                  <a
                    href={siteConfig.socialLinks.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full bg-[#5d6b3c] flex items-center justify-center text-white hover:bg-[#3c4a2a] transition-colors"
                    aria-label="Instagram"
                  >
                    <Instagram className="w-5 h-5" />
                  </a>
                  <a
                    href={siteConfig.socialLinks.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full bg-[#5d6b3c] flex items-center justify-center text-white hover:bg-[#3c4a2a] transition-colors"
                    aria-label="LinkedIn"
                  >
                    <Linkedin className="w-5 h-5" />
                  </a>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="border border-[#bd8cae]/30 shadow-lg">
                <CardContent className="p-8">
                  <h2 className="font-serif text-2xl text-[#3c4a2a] mb-6">
                    {c('contact.form.heading', 'Send Us a Message')}
                  </h2>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="name" className="text-[#3c4a2a]">
                          Full Name *
                        </Label>
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          className="mt-2 border-[#bd8cae]/50 focus:border-[#5d6b3c]"
                          placeholder="Your name"
                        />
                      </div>
                      <div>
                        <Label htmlFor="email" className="text-[#3c4a2a]">
                          Email Address *
                        </Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="mt-2 border-[#bd8cae]/50 focus:border-[#5d6b3c]"
                          placeholder="your@email.com"
                        />
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="phone" className="text-[#3c4a2a]">
                          Phone Number
                        </Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleChange}
                          className="mt-2 border-[#bd8cae]/50 focus:border-[#5d6b3c]"
                          placeholder="(xxx) xxx-xxxx"
                        />
                      </div>
                      <div>
                        <Label htmlFor="subject" className="text-[#3c4a2a]">
                          Subject *
                        </Label>
                        <Input
                          id="subject"
                          name="subject"
                          value={formData.subject}
                          onChange={handleChange}
                          required
                          className="mt-2 border-[#bd8cae]/50 focus:border-[#5d6b3c]"
                          placeholder="How can we help?"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="message" className="text-[#3c4a2a]">
                        Message *
                      </Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={6}
                        className="mt-2 border-[#bd8cae]/50 focus:border-[#5d6b3c]"
                        placeholder="Tell us more about your inquiry..."
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full sm:w-auto bg-[#5d6b3c] hover:bg-[#3c4a2a] text-white px-8 py-6"
                    >
                      {isSubmitting ? (
                        'Sending...'
                      ) : (
                        <>
                          Send Message
                          <Send className="w-4 h-4 ml-2" />
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ContactPage;
